







































































































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { Location as VueLocation } from "vue-router";
import _ from "@/boot/lodash";

// Mixins
import BrandMixin from "@/mixins/brandMixin";
import LoggedUserMixin from "@/mixins/loggedUserMixin";
import PaymentProvider from "@/components/app/global/payments/paymentProvider.vue";
import HasValidation from "@/mixins/HasValidation";

// Data/Enums
import { DataModules } from "@/store/modules/data";
import { GatewayContext } from "@/data/enums/gateway";
import { QUERY_PARAMS, BrandConfigKeys } from "@/data/constants";

// Types
import { IInvoice } from "@/models/invoices";
import { IPaymentDetail } from "@/models/paymentDetails";
import { ModalCancelByAll } from "@/data/enums";
import { ICurrency } from "@/models/constants";
import { IQuickAction } from "@/models/actions";

@Component({
  components: {
    "select-payment-method": () =>
      import("@/components/app/global/payments/selectPaymentMethodComp.vue")
  }
})
export default class InvoicePaymentModal extends Mixins(
  PaymentProvider,
  LoggedUserMixin,
  HasValidation,
  BrandMixin
) {
  // Props

  @Prop({ required: true })
  public invoice!: IInvoice;

  @Prop({ default: "" })
  public preselectedStoredPaymentMethodId!: IPaymentDetail["id"];

  @Prop({ default: false })
  public preselectedShowPendingPayment!: boolean;

  @Prop({ default: undefined })
  public externalAuthLocationFunc!: () => VueLocation;

  @Prop({ default: undefined })
  public preferredCurrencyCode?: ICurrency["code"];

  // Data

  public currencyCode = "";
  public currencyId = "";
  public hasRequestError = false;
  public isLoading = true;
  public isReloading = false;
  public newAmount = 0;
  public newAmountFormatted = "";
  public processing = {};
  public showPendingPayment = false;
  public unpaidAmount = 0;
  public unpaidAmountFormatted = "";

  // Life-cycle methods

  public async created() {
    this.initDataDefaults();
    this.loadData();
    this.showPendingPayment =
      this.preselectedShowPendingPayment || this.hasPendingPayment;
  }

  public initDataDefaults() {
    // Here we use 'payment_currency' as priority
    this.currencyCode =
      this.invoice?.payment_currency?.code ||
      this.preferredCurrencyCode ||
      this.invoice?.currency?.code;
    this.currencyId =
      this.invoice?.payment_currency_id || this.invoice?.currency_id;
    this.unpaidAmount = this.invoice?.unpaid_amount_converted;
    this.unpaidAmountFormatted = this.invoice?.unpaid_amount_formatted;
  }

  // Computed

  public get actions(): IQuickAction[] {
    return _.filter(
      [
        {
          label: this.$t("_action.change_currency"),
          method: () => this.initChangeCurrency(),
          if: this.canChangeCurrency
        },
        {
          label: this.$t("_action.change_amount"),
          method: () => this.initChangeAmount(),
          if: this.canChangeAmount
        },
        {
          label: this.$t("_action.pay_in_full"),
          method: () => this.payInFull(),
          if: this.computedPayload.amount !== this.unpaidAmount
        }
      ],
      action => action.if
    );
  }

  public get attemptKey() {
    return btoa(
      JSON.stringify({
        invoiceId: this.invoice.id,
        timestamp: this.$moment()
          .toDate()
          .getTime()
      })
    );
  }

  public get canChangeCurrency(): boolean {
    if (this.hasPartialPayment) return false;
    return (
      this.$store.getters["brand/config"][
        BrandConfigKeys.BILLING_DIFFERENT_CURRENCY_PAYMENT_ENABLED
      ] ?? false
    );
  }

  public get canChangeAmount(): boolean {
    return (
      this.$store.getters["brand/config"][
        BrandConfigKeys.PARTIAL_PAYMENTS_ENABLED
      ] || this.isAdmin
    );
  }

  public get computedPayload() {
    const useNew = this.newAmount > 0 && this.newAmount <= this.unpaidAmount;
    return {
      amount: useNew ? this.newAmount : this.unpaidAmount,
      amount_formatted: useNew
        ? this.newAmountFormatted
        : this.unpaidAmountFormatted,
      currency_code: this.currencyCode,
      currency_id: _.find(this.$store.state?.constants?.currencies, [
        "code",
        this.currencyCode
      ])?.id as ICurrency["id"]
    };
  }

  public get showCurrencyCode() {
    const { amount_formatted, currency_code } = this.computedPayload;
    if (amount_formatted.includes(currency_code)) return false;
    return true;
  }

  public get hasPendingPayment() {
    return !!this.invoice?.payments?.find(i => i.pending);
  }

  public get hasPartialPayment() {
    return !!this.invoice?.unpaid_amount && !!this.invoice?.paid_amount;
  }

  public get isProcessing() {
    return !!Object.keys(this.processing)?.length;
  }

  public get selectPaymentMethodBindings() {
    return {
      accountId: this.invoice.account_id,
      autoSelectFirstStoredPaymentMethod: !this
        .preselectedStoredPaymentMethodId,
      brandId: this.invoice.brand_id,
      canChangeManualPaymentAmount: false,
      cardHolderName: this.invoice.client?.fullname,
      clientId: this.invoice.client_id,
      currencyId: this.computedPayload.currency_id,
      currencyCode: this.computedPayload.currency_code,
      externalAuthReturnLocation: this.externalAuthReturnLocation,
      gatewayContext: GatewayContext.PAY,
      hasManualPayment: this.isAdmin,
      hasPartialPayment: false, // Pass false as this component handles capture of partial amount
      hasPayLater: false, // Not applicable in pay invoice context
      hidePaymentTypeSelector: this.isClient,
      invoiceCurrencyId: this.invoice.currency_id,
      isDisabled: this.isProcessing,
      onlineGatewaysOnly: false,
      preselectedStoredPaymentMethodId: this.preselectedStoredPaymentMethodId,
      totalAmount: this.computedPayload.amount
    };
  }

  // Computed 'PaymentProvider' Overrides

  public get additionalPaymentData() {
    const origin = window.location.origin;

    const returnRoute = this.$router.resolve(this.externalAuthReturnLocation)
      .route;

    const successLocation = _.merge({}, returnRoute, {
      query: {
        [QUERY_PARAMS.PAYMENT_SUCCESS]: "true",
        [QUERY_PARAMS.ATTEMPT]: this.attemptKey
      }
    });

    const failLocation = _.merge({}, returnRoute, {
      query: {
        [QUERY_PARAMS.PAYMENT_SUCCESS]: "false",
        [QUERY_PARAMS.ATTEMPT]: this.attemptKey
      }
    });

    const returnUrlEncodedSuccess = encodeURIComponent(
      `${origin}${successLocation.fullPath}`
    );

    const returnUrlEncodedFail = encodeURIComponent(
      `${origin}${failLocation.fullPath}`
    );

    return {
      client_id: this.invoice.client_id,
      account_id: this.invoice.account_id,
      invoice_id: this.invoice.id,
      return_url: `?${QUERY_PARAMS.SUCCESS}=${returnUrlEncodedSuccess}&${QUERY_PARAMS.FAILED}=${returnUrlEncodedFail}`,
      cancel_url: `${origin}${returnRoute.fullPath}`,
      currency_code: this.computedPayload.currency_code
    };
  }

  public get additionalManualPaymentData() {
    return {
      invoice_id: this.invoice.id,
      currency_code: this.computedPayload.currency_code
    };
  }

  public get externalAuthReturnLocation(): VueLocation {
    if (_.isFunction(this.externalAuthLocationFunc))
      return this.externalAuthLocationFunc();
    return this.buildResolver({
      returnLocation: this.$store.getters[
        `data/${DataModules.INVOICES}/getRoute`
      ](this.invoice.id, this.invoice.client_id),
      paymentMethodType: this.paymentMethodType,
      initPay: this.isExternalStore ? { invoiceId: this.invoice.id } : undefined
    }).location;
  }

  public get totalAmountToPay() {
    return this.invoice.unpaid_amount_converted;
  }

  // Methods

  public async loadData() {
    try {
      this.hasRequestError = false;
      await Promise.all([
        // Get brand config settings (in not cached already)
        this.getBrandSettings(),
        // Apply starting currency (could be locked or preferred currency)
        this.applyNewCurrency(this.currencyCode)
      ]);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getBrandSettings() {
    return this.$store.dispatch("brand/getConfig", {
      brandId: this.invoice?.brand_id,
      keys: [
        BrandConfigKeys.BILLING_DIFFERENT_CURRENCY_PAYMENT_ENABLED,
        BrandConfigKeys.PARTIAL_PAYMENTS_ENABLED
      ]
    });
  }

  public async pay() {
    this.$set(this.processing, "payment", true);
    try {
      if (!(await this.validate())) throw this.$t("_.form_invalid");
      // Prepare payment data
      await this.preparePaymentData(this.invoice.client_id);

      if (this.externalAuth)
        await this.redirectToExternalAuth(this.externalAuth);

      // Execute payment (may redirect off-site for SCA)
      await this.executePayment(this.attemptKey);
      // Emit 'success'
      this.$emit("success", this.invoice);
    } catch (error) {
      this.$emit("fail", this.invoice);
      this.$delete(this.processing, "payment");
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }
  }

  public async initChangeAmount() {
    if (!this.canChangeAmount) return;
    try {
      const newAmount = await this.captureNewAmount();
      if (!newAmount) return; // Abort if falsey
      this.$set(this.processing, "amount", true);
      const { total, total_formatted } = await this.$store.dispatch(
        "api/calculate",
        {
          prices: [newAmount],
          currency_code: this.currencyCode
        }
      );
      this.newAmount = total;
      this.newAmountFormatted = total_formatted;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.$delete(this.processing, "amount");
    }
  }

  public async captureNewAmount(): Promise<number | null> {
    return new Promise(resolve => {
      this.$store.dispatch("ui/open/windowModal", {
        config: {
          component: () =>
            import("@/components/app/global/payments/changeAmountModal.vue"),
          props: {
            amount: this.computedPayload.amount,
            maxAmount: this.unpaidAmount,
            maxAmountFormatted: this.unpaidAmountFormatted,
            currencyCode: this.computedPayload.currency_code
          },
          width: 440,
          canCancel: ModalCancelByAll,
          onCancel: () => resolve(null),
          events: {
            success: resolve
          }
        }
      });
    });
  }

  public payInFull() {
    this.newAmount = 0;
    this.newAmountFormatted = "";
  }

  /**
   * @name initChangeCurrency
   * @desc Here we initialise the process of changing currency
   */

  public async initChangeCurrency() {
    if (!this.canChangeCurrency) return;
    const currencyCode = await this.captureNewCurrency();
    if (currencyCode) this.applyNewCurrency(currencyCode);
  }

  /**
   * @name captureNewCurrency
   * @desc Here we capture a new brand currency through a modal flow
   */

  public async captureNewCurrency(): Promise<ICurrency["code"] | null> {
    return new Promise(resolve => {
      this.$store.dispatch("ui/open/windowModal", {
        config: {
          component: () =>
            import("@/components/app/global/payments/changeCurrencyModal.vue"),
          props: {
            brandId: this.invoice?.brand_id,
            currencyCode: this.currencyCode
          },
          width: 440,
          canCancel: ModalCancelByAll,
          onCancel: () => resolve(null),
          events: {
            success: resolve
          }
        }
      });
    });
  }

  /**
   * @name applyNewCurrency
   * @desc Here we accept a currency code and attempt to convert the unpaid
   * invoice balance using the new currency.
   */

  public async applyNewCurrency(code?: ICurrency["code"]) {
    if (!code) return;
    try {
      this.$set(this.processing, "currency", true);
      const {
        unpaid_amount,
        unpaid_amount_formatted
      } = await this.getNewCurrencyAmount(code);
      // Reset partial amounts
      this.newAmount = 0;
      this.newAmountFormatted = "";
      // Set new unpaid amounts
      this.unpaidAmount = unpaid_amount;
      this.unpaidAmountFormatted = unpaid_amount_formatted;
      // Set new currency code
      this.currencyCode = code;
      // Clear previously select payment method
      this.paymentMethod = null;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.$delete(this.processing, "currency");
    }
  }

  /**
   * @name getNewCurrencyAmount
   * @desc Here we get the unpaid invoice amount, converted for a given brand currency
   */

  public async getNewCurrencyAmount(currency_code) {
    return this.$store.dispatch(
      `data/${DataModules.INVOICES}/getUnpaidConvertedAmount`,
      {
        invoiceId: this.invoice.id,
        params: { currency_code },
        vm: this,
        storeData: false,
        returnData: true
      }
    );
  }

  // Watchers

  @Watch("paymentMethod", { deep: true, immediate: true })
  public onPaymentMethodChanged() {
    if (!_.isEmpty(this.paymentMethod)) {
      this.$emit("payment-method-selected", this.paymentMethod);
    }
  }
}
